import configs from '@/config'
// Sentry
import Vue from 'vue'
import * as Sentry from '@sentry/vue'
import { browserTracingIntegration } from '@sentry/vue'
import { Replay } from '@sentry/replay'
import { logger, getDeviceId } from '@ahaslides-product/frontend-toolkit'

const ignoreErrors = [
  'zaloJSV2 is not defined',
  'Can\'t find variable: zaloJSV2',
  'ResizeObserver loop limit exceeded',
  'Cannot read property \'getReadModeConfig\' of undefined',
  'Cannot read property \'getReadModeRender\' of undefined',
  'Cannot read property \'getReadModeExtract\' of undefined',
  'UnhandledRejection: Non-Error promise rejection captured with value: Object Not Found Matching Id:2',
]

const replay = new Replay({ maskAllText: false })

const init = () => {
  if (!configs.isSentryEnabled) return

  Sentry.init({
    release: configs.sentryRelease,
    dsn: configs.sentry,
    environment: configs.env,
    integrations: [
      browserTracingIntegration(),
      replay,
    ],
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
    tracesSampleRate: 0,
    Vue,
    ignoreErrors,
    denyUrls: [
      /analytics\.tiktok\.com/i,
    ],
    tracingOptions: {
      trackComponents: true,
    },
    enableLogs: true,
    beforeSend(event, hint) {
      const { originalException: error } = hint

      if (configs.env !== 'production') {
        // log error to browser console
        // for better visibility into exceptions
        if (event?.extra?.error) {
          // eslint-disable-next-line no-console
          logger(event.extra.error)
        } else if (error) {
          // eslint-disable-next-line no-console
          logger(error)
        }
      }
      // ignore sentry error due to network cut-off
      if (error?.status === 0) return null
      return event
    },
  })
}

const captureException = (error, options) => {
  if (!configs.isSentryEnabled) return

  try {
    // Preserve a real Error's stack; only synthesize one when handed a string/other.
    Sentry.captureException(error instanceof Error ? error : new Error(error), options)
  } catch {
    // do nothing
  }
}

const addBreadcrumb = (breadcrumb = {}) => {
  if (!configs.isSentryEnabled) return

  try {
    const editedBreadcrumb = { ...breadcrumb }

    if (breadcrumb.level === 'info') {
      editedBreadcrumb.level = 'info'
    }

    Sentry.addBreadcrumb(editedBreadcrumb)
  } catch {
    // do nothing
  }
}

const startSessionReplay = async () => {
  if (!configs.isSentryEnabled) return

  try {
    await replay.flush()
  } catch (error) {
    captureException(error)
  }
}

const stopSessionReplay = async () => {
  if (!configs.isSentryEnabled) return

  try {
    await replay.stop()
  } catch (error) {
    captureException(error)
  }
}

const getTabId = () => {
  let tabId = sessionStorage.getItem('aha-tab-id')
  if (!tabId) {
    tabId = new Date().getTime()
    sessionStorage.setItem('aha-tab-id', tabId)
  }
  return tabId
}

const augmentData = (data) => {
  if (!data) return data
  return {
    ...data,
    deviceId: getDeviceId(),
    tabId: getTabId(),
  }
}

const setTags = (attributes = {}) => {
  if (!configs.isSentryEnabled) return

  try {
    // Set as tags for filtering and searching
    Object.entries(attributes).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        Sentry.setTag(key, value)
      }
    })
  } catch (error) {
    // do nothing
    logger('error setting sentry tags', error)
  }
}

export const logInfo = (message, data) => {
  if (!configs.isSentryEnabled) return
  Sentry.logger.info(message, augmentData(data))
  // also log to file
  logger(message, data)
}

export const logWarn = (message, data) => {
  if (!configs.isSentryEnabled) return
  Sentry.logger.warn(message, augmentData(data))
  // also log to file
  logger(message, data)
}

export default {
  init,
  captureException,
  addBreadcrumb,
  startSessionReplay,
  stopSessionReplay,
  info: logInfo,
  warn: logWarn,
  setTags,
}
